import {
    Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, Grid, InputLabel, LinearProgress, MenuItem,
    Select, Stack, Switch, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import CustomAxios from '../../utils/CustomAxios';
import {
    CTA_TARGETS, SAMPLE_PERSON, TEMPLATES, THEMES, TOKENS, blankMoment, fromServer,
    fromTemplate, localErrors, payload, rate, renderTokens, rewardText, tokenContext,
    STATUS_LABELS, actionsFor, readableTitle, momentTitle, PLAN_LENGTHS, fullRewardText,
    audienceHubIds, planBreakdown, planCoverage, planOptionLabel,
} from '../../utils/moments';
import { CustomerPicker } from './customer_picker';
import { ProductPicker } from './product_picker';

/**
 * Moments Studio: design a rich message once, and it reaches each customer as a
 * push with a picture, an instant in-app popup and a bell entry -- in their own
 * words ({first_name}, {orders_count}...) and optionally with bonus points.
 *
 * Reach, points cost and the personalised samples all come from the server's
 * preview, which runs the same audience query as the send. The phone on the
 * right is the real layout the app draws, in the same four themes.
 */

const PINK = '#ED1E79';
const INK = '#16121A';
const MUTED = '#6E6478';
const LINE = '#E9E3EE';

const AUDIENCES = [
    { key: 'default', label: 'Everyone in the hubs' },
    { key: 'subscribers', label: 'Active subscribers' },
    { key: 'expired_subscribers', label: 'Past subscribers' },
    { key: 'non_subscribers', label: 'Never subscribed' },
];

const SEGMENTS = [
    { key: 'Active Recurring User', label: 'Active recurring' },
    { key: 'Repeat User', label: 'Repeat' },
    { key: 'Single Order User', label: 'Single order' },
    { key: 'Churned User', label: 'Churned' },
    { key: 'Never Ordered', label: 'Never ordered' },
];

const STEPS = ['Design', 'Audience', 'Reward', 'Delivery'];
const POINT_PRESETS = [0, 50, 100, 200, 500];
const BONUS_PRESETS = [0, 20, 50, 100, 200];

/** "80,000 pts + ₹20,000" from a preview, or '' when nothing is given. */
const costText = (preview) => {
    if (!preview) return '';
    const parts = [];
    if (preview.points_total) parts.push(`${preview.points_total.toLocaleString('en-IN')} pts`);
    if (Number(preview.bonus_total) > 0) parts.push(`₹${Number(preview.bonus_total).toLocaleString('en-IN')}`);
    return parts.join(' + ');
};

const Section = ({ title, hint, children }) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: INK }}>{title}</Typography>
        {hint ? <Typography sx={{ fontSize: 12, color: MUTED, mb: 1 }}>{hint}</Typography> : <Box sx={{ mb: 1 }} />}
        {children}
    </Box>
);

/* ------------------------------------------------------------------ phone */

function MomentCard({ m, person, claimed, onClaim, planName }) {
    const offer = fullRewardText(m, planName);
    const t = THEMES[m.theme] || THEMES.shino;
    const ctx = tokenContext(person, m);
    const sheet = m.layout === 'sheet';
    return (
        <Box sx={{
            position: 'absolute', inset: 0, bgcolor: 'rgba(10,6,14,.55)', display: 'flex',
            alignItems: sheet ? 'flex-end' : 'center', justifyContent: 'center', p: sheet ? 0 : 2,
        }}>
            <Box sx={{
                width: '100%', bgcolor: t.card, color: t.ink, overflow: 'hidden',
                borderRadius: sheet ? '22px 22px 0 0' : '22px', boxShadow: '0 18px 40px rgba(0,0,0,.35)',
                animation: 'momentIn .35s cubic-bezier(.2,.9,.3,1.2)',
                '@keyframes momentIn': { from: { transform: sheet ? 'translateY(40px)' : 'scale(.9)', opacity: 0 }, to: { transform: 'none', opacity: 1 } },
            }}>
                <Box sx={{ height: 150, background: t.bg, position: 'relative' }}>
                    {m.image_url ? (
                        <Box component="img" src={m.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                        <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', color: '#fff', opacity: .8 }}>
                            <AutoAwesomeOutlinedIcon sx={{ fontSize: 48 }} />
                        </Box>
                    )}
                    {!!offer && (
                        <Box sx={{ position: 'absolute', left: 12, bottom: -14, bgcolor: t.pts, color: t.ptsInk, px: 1.5, py: .5, borderRadius: 99, fontWeight: 800, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}>
                            +{offer}
                        </Box>
                    )}
                </Box>
                <Box sx={{ p: 2, pt: 3 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.25, color: t.ink }}>
                        {renderTokens(m.headline, ctx) || 'Your headline'}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, mt: .75, opacity: .85, color: t.ink, whiteSpace: 'pre-line' }}>
                        {renderTokens(m.message, ctx) || 'Your message'}
                    </Typography>
                    <Box
                        role="button"
                        onClick={onClaim}
                        sx={{ mt: 2, background: t.cta, color: t.ctaInk, borderRadius: 99, textAlign: 'center', py: 1.2, fontWeight: 800, fontSize: 14, cursor: 'pointer', userSelect: 'none' }}
                    >
                        {claimed && offer ? `✓ ${offer} added` : (renderTokens(m.cta_text, ctx) || 'Open')}
                    </Box>
                    <Typography sx={{ textAlign: 'center', fontSize: 12, mt: 1, opacity: .6, color: t.ink }}>Not now</Typography>
                </Box>
            </Box>
        </Box>
    );
}

function PhonePreview({ m, person, mode, planName }) {
    const [claimed, setClaimed] = useState(false);
    useEffect(() => { setClaimed(false); }, [m.reward_points, m.reward_bonus_amount, mode, m.template]);
    const ctx = tokenContext(person, m);
    return (
        <Box sx={{
            width: '100%', maxWidth: 300, mx: 'auto', aspectRatio: '9 / 18.5', borderRadius: '38px',
            border: '10px solid #1B1720', bgcolor: '#F6F3F8', position: 'relative', overflow: 'hidden',
            boxShadow: '0 20px 50px rgba(22,18,26,.25)',
        }}>
            <Box sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 80, height: 20, bgcolor: '#1B1720', borderRadius: 99, zIndex: 3 }} />
            {/* The app behind */}
            <Box sx={{ p: 1.5, pt: 5 }}>
                <Box sx={{ height: 34, borderRadius: 2, bgcolor: '#fff', mb: 1.2 }} />
                <Box sx={{ height: 90, borderRadius: 3, background: 'linear-gradient(120deg,#FFE3D0,#FFD1E6)', mb: 1.2 }} />
                <Stack direction="row" spacing={1}>
                    {[0, 1, 2].map((i) => <Box key={i} sx={{ flex: 1, height: 70, borderRadius: 2, bgcolor: '#fff' }} />)}
                </Stack>
                <Box sx={{ height: 120, borderRadius: 3, bgcolor: '#fff', mt: 1.2 }} />
            </Box>

            {mode === 'push' && (
                <Box sx={{ position: 'absolute', top: 36, left: 8, right: 8, bgcolor: 'rgba(255,255,255,.96)', borderRadius: 3, p: 1.2, boxShadow: '0 8px 24px rgba(0,0,0,.18)', animation: 'pushIn .4s ease', '@keyframes pushIn': { from: { transform: 'translateY(-30px)', opacity: 0 }, to: { transform: 'none', opacity: 1 } } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 18, height: 18, borderRadius: 1, background: THEMES.shino.bg }} />
                        <Typography sx={{ fontSize: 11, color: MUTED, flex: 1 }}>Shino · now</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: .5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{renderTokens(m.headline, ctx) || 'Your headline'}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#444', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{renderTokens(m.message, ctx)}</Typography>
                        </Box>
                        {m.image_url && <Box component="img" src={m.image_url} alt="" sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover' }} />}
                    </Stack>
                    {m.image_url && <Box component="img" src={m.image_url} alt="" sx={{ mt: 1, width: '100%', height: 110, borderRadius: 2, objectFit: 'cover' }} />}
                </Box>
            )}

            {mode === 'popup' && <MomentCard m={m} person={person} claimed={claimed} planName={planName} onClaim={() => setClaimed(true)} />}

            {mode === 'bell' && (
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#fff', pt: 5, px: 1.5 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>Notifications</Typography>
                    <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#FFF7FB' }}>
                        {m.image_url && <Box component="img" src={m.image_url} alt="" sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />}
                        <Box sx={{ p: 1.2 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{renderTokens(m.headline, ctx) || 'Your headline'}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#444' }}>{renderTokens(m.message, ctx)}</Typography>
                            <Typography sx={{ fontSize: 11, color: MUTED, mt: .5 }}>Just now</Typography>
                        </Box>
                    </Box>
                    {[0, 1].map((i) => <Box key={i} sx={{ height: 54, borderRadius: 3, bgcolor: '#F4F1F6', mt: 1 }} />)}
                </Box>
            )}
        </Box>
    );
}

/* ------------------------------------------------------------------ list */

const STATUS_STYLE = {
    draft: { bg: '#FFFFFF', fg: MUTED },
    scheduled: { bg: '#E3EEFF', fg: '#1D4ED8' },
    delivering: { bg: '#FFF4DC', fg: '#8A5A00' },
    live: { bg: '#E3F4EE', fg: '#0B7A5E' },
    ended: { bg: '#F1ECF4', fg: MUTED },
    cancelled: { bg: '#F1ECF4', fg: MUTED },
};

const ACTION_LABELS = { edit: 'Edit', duplicate: 'Duplicate', stop: 'Stop popup', delete: 'Delete' };

/** What each confirmation says. Every consequence is spelled out. */
export const CONFIRM_COPY = {
    delete: (r) => ({
        title: 'Delete this moment?',
        body: r.display_status === 'draft' ? 'This draft will be removed.' : 'It disappears from the list, and customers stop seeing it.',
        impact: r.display_status === 'draft' || r.display_status === 'scheduled'
            ? [['✕', r.display_status === 'scheduled' ? 'It will not go out' : 'The draft is removed']]
            : [['✕', 'The popup stops for everyone who has not opened it'], ['✕', 'Its bell entries are removed'],
                ['✓', 'Rewards already claimed stay with the customer'], ['✕', 'Its stats are deleted']],
        ok: 'Delete',
        danger: true,
    }),
    stop: () => ({
        title: 'Stop the popup?',
        body: "The moment stays in the list and in customers' bells.",
        impact: [['✕', 'The popup stops showing from now'], ['✓', 'Bell entries stay'], ['✓', 'Claimed rewards and stats stay']],
        ok: 'Stop popup',
        danger: true,
    }),
    pushnow: () => ({
        title: 'Send the push now?',
        body: 'It was held because it was sent during quiet hours (9 PM to 8 AM).',
        impact: [['↗', "Goes to the phones of customers who haven't had it yet"], ['✓', 'Popup and bell are unchanged']],
        ok: 'Send push now',
    }),
    editlive: () => ({
        title: 'Edit a live moment',
        body: 'You can change the image, words, button, look and when the popup stops.',
        impact: [['↻', "Customers who haven't opened it yet see the new version"], ['🔒', 'Audience and reward are locked. Duplicate it to change those.']],
        ok: 'Open editor',
    }),
};

export function ConfirmDialog({ open, copy, busy, onCancel, onConfirm }) {
    if (!copy) return null;
    return (
        <Dialog open={open} onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ fontWeight: 800, pb: .5 }}>{copy.title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ color: MUTED, fontSize: 14 }}>{copy.body}</Typography>
                {!!copy.impact?.length && (
                    <Stack spacing={.75} sx={{ mt: 1.5 }}>
                        {copy.impact.map(([icon, text]) => (
                            <Stack key={text} direction="row" spacing={1.25} alignItems="flex-start">
                                <Box sx={{ width: 18, textAlign: 'center', fontWeight: 700, color: icon === '✓' ? '#0B7A5E' : icon === '✕' ? '#B42318' : INK }}>{icon}</Box>
                                <Typography sx={{ fontSize: 14 }}>{text}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={onConfirm} disabled={busy} autoFocus
                    sx={copy.danger ? { bgcolor: '#B42318', '&:hover': { bgcolor: '#8F1C13' } } : { bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' } }}>
                    {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : copy.ok}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function MomentList({ rows, loading, onNew, onAction }) {
    if (loading && !rows.length) return <LinearProgress />;
    if (!rows.length) {
        return (
            <Box sx={{ textAlign: 'center', py: 8, border: `1px dashed ${LINE}`, borderRadius: 3 }}>
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 44, color: PINK }} />
                <Typography sx={{ fontWeight: 700, mt: 1 }}>No moments yet</Typography>
                <Typography sx={{ color: MUTED, fontSize: 14, mb: 2 }}>Thank your regulars, treat your members, win back quiet customers.</Typography>
                <Button variant="contained" onClick={onNew} sx={{ bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' } }}>Create a moment</Button>
            </Box>
        );
    }
    return (
        <Grid container spacing={2}>
            {rows.map((r) => {
                const s = r.stats || {};
                const t = THEMES[r.theme] || THEMES.shino;
                const status = r.display_status || r.status;
                const look = STATUS_STYLE[status] || STATUS_STYLE.draft;
                const given = [rewardText(s.points, s.bonus), s.plans ? `${s.plans} plan${s.plans === 1 ? '' : 's'}` : ''].filter(Boolean).join(' + ');
                return (
                    <Grid item xs={12} sm={6} lg={4} key={r.id}>
                        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ height: 96, background: t.bg, position: 'relative' }}>
                                {r.image_url && <Box component="img" src={r.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                                <Chip size="small" label={STATUS_LABELS[status] || status}
                                    icon={['delivering', 'live'].includes(status) ? <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: look.fg, ml: '8px !important', ...(status === 'delivering' ? { animation: 'blink 1s infinite', '@keyframes blink': { '50%': { opacity: .25 } } } : {}) }} /> : undefined}
                                    sx={{ position: 'absolute', top: 8, right: 8, fontWeight: 700, bgcolor: look.bg, color: look.fg }} />
                            </Box>
                            <Box sx={{ p: 1.5, flex: 1 }}>
                                <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{momentTitle(r)}</Typography>
                                <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>
                                    {status === 'scheduled' && r.scheduled_at ? `Goes out ${new Date(r.scheduled_at).toLocaleString('en-IN')}` : (r.audience_label || '')}
                                </Typography>
                                {r.stats && (
                                    <Grid container spacing={1} sx={{ mt: .5 }}>
                                        {[
                                            ['Sent', s.total],
                                            ['Pushed', s.pushed],
                                            ['Seen', `${s.shown} · ${rate(s.shown, s.total)}`],
                                            ['Tapped', `${s.clicked} · ${rate(s.clicked, s.total)}`],
                                            ['Claimed', s.claimed],
                                            ['Given', given || '—'],
                                        ].map(([k, v]) => (
                                            <Grid item xs={4} key={k}>
                                                <Typography sx={{ fontSize: 11, color: MUTED }}>{k}</Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{v}</Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                {!!r.send_error && status === 'draft' && (
                                    <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: 12 }}>{r.send_error}</Alert>
                                )}
                                {r.push_deferred_until && status === 'live' && (
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap"
                                        sx={{ mt: 1, px: 1, py: .5, borderRadius: 2, bgcolor: '#FFF4DC', color: '#8A5A00', gap: .5 }}>
                                        <Typography sx={{ fontSize: 12 }}>
                                            Push held for quiet hours until {new Date(r.push_deferred_until).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                        </Typography>
                                        <Button size="small" onClick={() => onAction('pushnow', r)} sx={{ color: '#8A5A00', fontWeight: 700 }}>Send push now</Button>
                                    </Stack>
                                )}
                            </Box>
                            <Stack direction="row" sx={{ p: 1.5, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                                {actionsFor(status).map((a) => (
                                    <Button key={a} size="small" color={a === 'delete' || a === 'stop' ? 'error' : 'primary'} onClick={() => onAction(a, r)}>
                                        {ACTION_LABELS[a]}
                                    </Button>
                                ))}
                            </Stack>
                        </Box>
                    </Grid>
                );
            })}
        </Grid>
    );
}

/* ---------------------------------------------------------------- studio */

export function MomentsStudio() {
    const theme = useTheme();
    const wide = useMediaQuery(theme.breakpoints.up('lg'));
    const narrow = useMediaQuery(theme.breakpoints.down('sm'));
    const toast = useRef(null);

    const [view, setView] = useState('list');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [options, setOptions] = useState({ hubs: [], plans: [] });

    const [m, setM] = useState(blankMoment);
    const [editingId, setEditingId] = useState(null);
    const [step, setStep] = useState(0);
    const [mode, setMode] = useState('popup');
    const [personIdx, setPersonIdx] = useState(0);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [serverErrors, setServerErrors] = useState({});
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmCost, setConfirmCost] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [testMobile, setTestMobile] = useState('');
    const [sendOpen, setSendOpen] = useState(false);
    const [liveEdit, setLiveEdit] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind, row }
    const [confirmBusy, setConfirmBusy] = useState(false);

    const set = (patch) => setM((prev) => ({ ...prev, ...patch }));
    const errors = useMemo(() => ({ ...localErrors(m), ...serverErrors }), [m, serverErrors]);
    const say = (severity, summary, detail) => toast.current?.show({ severity, summary, detail, life: 4500 });

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const [list, opts] = await Promise.all([CustomAxios.get('moments/'), CustomAxios.get('moments/options/')]);
            setRows(list.data?.results || []);
            setOptions(opts.data || { hubs: [], plans: [] });
        } catch (e) {
            say('error', 'Could not load moments', e?.response?.data?.detail || 'Check your connection and permissions.');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // While anything is still going out, refresh quietly until it is Live.
    const delivering = rows.some((r) => r.display_status === 'delivering');
    useEffect(() => {
        if (view !== 'list' || !delivering) return undefined;
        const timer = setInterval(() => load({ silent: true }), 3000);
        return () => clearInterval(timer);
    }, [view, delivering, load]);

    // Live reach, cost and personalised samples, debounced.
    useEffect(() => {
        if (view !== 'edit' || uploading) return undefined;
        if (Object.keys(localErrors(m)).length) return undefined;
        const handle = setTimeout(async () => {
            setPreviewing(true);
            try {
                const res = await CustomAxios.post('moments/preview/', payload(m));
                setPreview(res.data);
                // Hub checks (plan, product) come back with the numbers.
                setServerErrors(res.data?.errors || {});
            } catch (e) {
                setServerErrors(e?.response?.data?.errors || {});
            } finally {
                setPreviewing(false);
            }
        }, 600);
        return () => clearTimeout(handle);
    }, [m, view, uploading]);

    const chosenPlan = (options.plans || []).find((p) => p.id === m.reward_plan_id) || null;
    const planName = chosenPlan?.name || m.reward_plan_name || '';
    const hubIds = audienceHubIds(m, preview, options.hubs);
    const blockers = Object.values(preview?.errors || {});
    const hubName = (options.hubs || []).find((h) => m.inventory_ids?.includes(h.id))?.name;
    const persons = useMemo(() => {
        const fromServerSamples = (preview?.samples || []).map((s) => ({
            ...SAMPLE_PERSON,
            first_name: s.first_name,
            orders_count: s.orders_count ?? SAMPLE_PERSON.orders_count,
            plan_name: s.plan_name || SAMPLE_PERSON.plan_name,
            hub: s.hub || SAMPLE_PERSON.hub,
        }));
        return fromServerSamples.length ? fromServerSamples : [{ ...SAMPLE_PERSON, hub: hubName || SAMPLE_PERSON.hub }];
    }, [preview, hubName]);
    const person = persons[Math.min(personIdx, persons.length - 1)];

    const startNew = () => { setM(blankMoment()); setEditingId(null); setLiveEdit(false); setStep(0); setPreview(null); setConfirmCost(false); setView('edit'); };
    const edit = (row) => {
        setM(fromServer(row)); setEditingId(row.id); setLiveEdit(row.display_status === 'live' || row.display_status === 'ended');
        setStep(0); setPreview(null); setConfirmCost(false); setView('edit');
    };
    const duplicate = (row) => {
        const copy = fromServer(row);
        delete copy.id;
        // An unnamed moment (named after its headline) stays unnamed, so its
        // list title keeps following who it goes to.
        const unnamed = !row.name || row.name === String(row.headline || '').slice(0, 120).trim();
        setM({ ...copy, name: unnamed ? '' : `${row.name} (copy)`, scheduled_at: '', popup_until: '' });
        setEditingId(null); setLiveEdit(false); setStep(0); setPreview(null); setView('edit');
    };

    const onAction = (kind, row) => {
        if (kind === 'duplicate') { duplicate(row); return; }
        if (kind === 'edit') {
            if (['live', 'ended'].includes(row.display_status)) setConfirm({ kind: 'editlive', row });
            else edit(row);
            return;
        }
        setConfirm({ kind, row });
    };

    const runConfirm = async () => {
        const { kind, row } = confirm;
        if (kind === 'editlive') { setConfirm(null); edit(row); return; }
        setConfirmBusy(true);
        try {
            if (kind === 'delete') {
                await CustomAxios.delete(`moments/${row.id}/`);
                say('success', 'Deleted', momentTitle(row));
            } else if (kind === 'stop') {
                await CustomAxios.post(`moments/${row.id}/stop/`, {});
                say('success', 'Popup stopped');
            } else if (kind === 'pushnow') {
                await CustomAxios.post(`moments/${row.id}/push-now/`, {});
                say('success', 'Push is going out');
            }
            setConfirm(null);
            load({ silent: true });
        } catch (e) {
            say('error', 'Not done', e?.response?.data?.error || 'Try again.');
        } finally {
            setConfirmBusy(false);
        }
    };

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) { say('warn', 'Image too large', 'Keep it under 2 MB.'); return; }
        const local = URL.createObjectURL(file);
        set({ image_url: local });
        setUploading(true);
        try {
            const body = new FormData();
            body.append('image', file);
            const res = await CustomAxios.post('moments/upload-image/', body);
            set({ image_url: res.data.url });
        } catch (e) {
            set({ image_url: '' });
            say('error', 'Upload failed', e?.response?.data?.error || 'Try again.');
        } finally {
            setUploading(false);
            URL.revokeObjectURL(local);
        }
    };

    const save = async () => {
        if (Object.keys(localErrors(m)).length) { say('warn', 'Fix the highlighted fields'); return null; }
        if (uploading) { say('warn', 'Wait for the image to finish uploading'); return null; }
        setBusy(true);
        try {
            const res = editingId
                ? await CustomAxios.put(`moments/${editingId}/`, payload(m))
                : await CustomAxios.post('moments/', payload(m));
            setEditingId(res.data.id);
            if (liveEdit) {
                const n = Number(res.data.updated_copies) || 0;
                say('success', 'Saved', n ? `${n} customer${n === 1 ? '' : 's'} who haven't opened it will see the new version.` : 'Everyone has already seen it; new copies are unchanged.');
            }
            setServerErrors({});
            return res.data;
        } catch (e) {
            setServerErrors(e?.response?.data?.errors || {});
            say('error', 'Not saved', Object.values(e?.response?.data?.errors || {})[0] || '');
            return null;
        } finally {
            setBusy(false);
        }
    };

    const saveDraft = async () => {
        const saved = await save();
        if (saved) { say('success', 'Draft saved'); load(); }
    };

    const sendTest = async () => {
        const saved = await save();
        if (!saved) return;
        try {
            const res = await CustomAxios.post(`moments/${saved.id}/test/`, testMobile ? { mobileno: testMobile } : {});
            const d = res.data || {};
            say(d.popup || d.push ? 'success' : 'warn', 'Test sent',
                `${d.popup ? 'Popup sent to the open app. ' : 'App not open, so no popup. '}${d.push ? 'Push delivered.' : 'No push (no device token).'}`);
            setTestOpen(false);
        } catch (e) {
            say('error', 'Test failed', e?.response?.data?.error || '');
        }
    };

    const send = async () => {
        const saved = await save();
        if (!saved) return;
        setBusy(true);
        try {
            const res = await CustomAxios.post(`moments/${saved.id}/send/`, { confirm_cost: confirmCost });
            const d = res.data || {};
            say('success', d.status === 'scheduled' ? 'Scheduled' : 'Sent',
                d.status === 'scheduled' ? `Will reach about ${d.reach} customers.` : `Going to ${d.reach} customers${d.push_deferred_until ? '. Push waits for 8 AM (quiet hours).' : '.'}`);
            setSendOpen(false);
            setView('list');
            load();
        } catch (e) {
            say('error', 'Not sent', e?.response?.data?.error || '');
        } finally {
            setBusy(false);
        }
    };

    /* ---------------- steps ---------------- */

    const insertToken = (field, token) => set({ [field]: `${m[field] || ''}{${token}}` });

    const design = (
        <>
            <Section title="Start from" hint="Picks words, colours and audience. You can change everything.">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1 }}>
                    {TEMPLATES.map((tpl) => (
                        <Box key={tpl.id} role="button" onClick={() => setM((prev) => fromTemplate(tpl, prev))}
                            sx={{ cursor: 'pointer', border: `2px solid ${m.template === tpl.id ? PINK : LINE}`, borderRadius: 2.5, p: 1.2, bgcolor: m.template === tpl.id ? '#FFF3F8' : '#fff' }}>
                            <Box sx={{ height: 8, borderRadius: 9, background: THEMES[tpl.theme].bg, mb: 1 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{tpl.name}</Typography>
                            <Typography sx={{ fontSize: 11.5, color: MUTED }}>{tpl.desc}</Typography>
                        </Box>
                    ))}
                </Box>
            </Section>

            <Section title="Image" hint="JPG, PNG, WebP or GIF under 2 MB. Wide images (2:1) look best.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <Box sx={{ width: { xs: '100%', sm: 180 }, height: 90, borderRadius: 2, overflow: 'hidden', border: `1px dashed ${LINE}`, background: m.image_url ? undefined : (THEMES[m.theme] || THEMES.shino).bg, position: 'relative' }}>
                        {m.image_url && <Box component="img" src={m.image_url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        {uploading && <CircularProgress size={24} sx={{ position: 'absolute', top: 'calc(50% - 12px)', left: 'calc(50% - 12px)', color: '#fff' }} />}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button component="label" variant="outlined" startIcon={<AddPhotoAlternateOutlinedIcon />} disabled={uploading}>
                            {m.image_url ? 'Replace' : 'Upload'}
                            <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
                        </Button>
                        {m.image_url && <Button color="inherit" onClick={() => set({ image_url: '' })}>Remove</Button>}
                    </Stack>
                </Stack>
                {errors.image_url && <Typography sx={{ color: 'error.main', fontSize: 12, mt: .5 }}>{errors.image_url}</Typography>}
            </Section>

            <Section title="Words" hint="Tap a tag to add it. Each customer sees their own name, orders, plan and hub.">
                <Stack spacing={1.5}>
                    <TextField label="Headline" size="small" fullWidth value={m.headline} onChange={(e) => set({ headline: e.target.value })}
                        error={!!errors.headline} helperText={errors.headline || `${(m.headline || '').length}/140`} inputProps={{ maxLength: 140 }} />
                    <TextField label="Message" size="small" fullWidth multiline minRows={3} value={m.message} onChange={(e) => set({ message: e.target.value })}
                        error={!!errors.message} helperText={errors.message || `${(m.message || '').length}/600`} inputProps={{ maxLength: 600 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75 }}>
                        {TOKENS.map((tk) => <Chip key={tk} size="small" label={`{${tk}}`} onClick={() => insertToken('message', tk)} sx={{ fontFamily: 'monospace' }} />)}
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField label="Button text" size="small" fullWidth value={m.cta_text} onChange={(e) => set({ cta_text: e.target.value })} inputProps={{ maxLength: 40 }} />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Button opens</InputLabel>
                            <Select label="Button opens" value={m.cta_target} onChange={(e) => set({ cta_target: e.target.value })}>
                                {CTA_TARGETS.map((c) => <MenuItem key={c.value || 'none'} value={c.value}>{c.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                    {m.cta_target === 'ProductDescription' && (
                        <ProductPicker
                            value={m.cta_value}
                            onChange={(id) => set({ cta_value: id })}
                            hubIds={hubIds}
                            product={preview?.product}
                            error={errors.cta_value}
                        />
                    )}
                </Stack>
            </Section>

            <Section title="Look">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <ToggleButtonGroup size="small" exclusive value={m.layout} onChange={(_, v) => v && set({ layout: v })}>
                        <ToggleButton value="card">Centre card</ToggleButton>
                        <ToggleButton value="sheet">Bottom sheet</ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row" spacing={1}>
                        {Object.entries(THEMES).map(([id, t]) => (
                            <Box key={id} title={t.name} role="button" onClick={() => set({ theme: id })}
                                sx={{ width: 34, height: 34, borderRadius: '50%', background: t.bg, cursor: 'pointer', outline: m.theme === id ? `3px solid ${INK}` : 'none', outlineOffset: 2 }} />
                        ))}
                    </Stack>
                </Stack>
            </Section>
            <TextField label="Internal name (optional)" size="small" fullWidth value={m.name} onChange={(e) => set({ name: e.target.value })} helperText="Only admins see this." />
        </>
    );

    const pickMode = m.audience_mode === 'pick';
    const audience = (
        <>
            <Section title="Who gets it">
                <ToggleButtonGroup size="small" exclusive value={m.audience_mode || 'filters'} orientation={narrow ? 'vertical' : 'horizontal'} fullWidth={narrow}
                    onChange={(_, v) => v && set({ audience_mode: v })}>
                    <ToggleButton value="filters">Everyone matching filters</ToggleButton>
                    <ToggleButton value="pick">Only customers I pick</ToggleButton>
                </ToggleButtonGroup>
                {errors.user_ids && <Typography sx={{ color: 'error.main', fontSize: 12, mt: .5 }}>{errors.user_ids}</Typography>}
            </Section>
            {!pickMode && (<>
            <Section title="Hubs" hint="None selected means every hub.">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {(options.hubs || []).map((h) => {
                        const on = m.inventory_ids.includes(h.id);
                        // With a free plan chosen, say where it can't be given.
                        const noPlan = chosenPlan && planCoverage(chosenPlan, [h.id]) === 'none';
                        return <Chip key={h.id} label={noPlan ? `${h.name} · no ${chosenPlan.name}` : h.name} color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                            onClick={() => set({ inventory_ids: on ? m.inventory_ids.filter((x) => x !== h.id) : [...m.inventory_ids, h.id] })} />;
                    })}
                </Box>
            </Section>
            <Section title="Subscription">
                <ToggleButtonGroup size="small" exclusive value={m.audience} orientation={narrow ? 'vertical' : 'horizontal'} fullWidth={narrow}
                    onChange={(_, v) => v && set({ audience: v, subscription_plan_ids: v === 'default' || v === 'non_subscribers' ? [] : m.subscription_plan_ids })}>
                    {AUDIENCES.map((a) => <ToggleButton key={a.key} value={a.key}>{a.label}</ToggleButton>)}
                </ToggleButtonGroup>
                {(m.audience === 'subscribers' || m.audience === 'expired_subscribers') && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                        <Typography sx={{ fontSize: 12, color: MUTED, width: '100%' }}>Only these plans (none = any plan):</Typography>
                        {(options.plans || []).map((p) => {
                            const on = m.subscription_plan_ids.includes(p.id);
                            return <Chip key={p.id} label={p.name} size="small" color={on ? 'secondary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                onClick={() => set({ subscription_plan_ids: on ? m.subscription_plan_ids.filter((x) => x !== p.id) : [...m.subscription_plan_ids, p.id] })} />;
                        })}
                    </Box>
                )}
            </Section>
            <Section title="Order history" hint="Any ticked group matches. None ticked means everyone.">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {SEGMENTS.map((s) => {
                        const on = m.segments.includes(s.key);
                        return <Chip key={s.key} label={s.label} color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                            onClick={() => set({ segments: on ? m.segments.filter((x) => x !== s.key) : [...m.segments, s.key] })} />;
                    })}
                </Box>
            </Section>
            </>)}
            <Section title="Customers" hint={pickMode ? 'Search by name or mobile and tick who gets it.' : 'Everyone below is included. Untick anyone to leave them out.'}>
                <CustomerPicker m={m} set={set} />
            </Section>
        </>
    );

    const reward = (
        <>
            <Section title="Bonus loyalty points per customer" hint="0 means no reward: a message only.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <TextField size="small" type="number" label="Points" value={m.reward_points} sx={{ width: { xs: '100%', sm: 140 } }}
                        onChange={(e) => set({ reward_points: e.target.value })} error={!!errors.reward_points} helperText={errors.reward_points}
                        inputProps={{ min: 0, max: 5000, step: 10 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75 }}>
                        {POINT_PRESETS.map((p) => <Chip key={p} label={p || 'None'} onClick={() => set({ reward_points: p })} color={Number(m.reward_points) === p ? 'primary' : 'default'} />)}
                    </Box>
                </Stack>
            </Section>
            <Section title="Wallet bonus (₹) per customer" hint="Promotional money in the customer's wallet, the same as a bonus grant. Spent first at checkout, and it expires. 0 means none.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                    <TextField size="small" type="number" label="Amount (₹)" value={m.reward_bonus_amount} sx={{ width: { xs: '100%', sm: 140 } }}
                        onChange={(e) => set({ reward_bonus_amount: e.target.value })} error={!!errors.reward_bonus_amount} helperText={errors.reward_bonus_amount}
                        inputProps={{ min: 0, max: 5000, step: 10 }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .75 }}>
                        {BONUS_PRESETS.map((p) => <Chip key={p} label={p ? `₹${p}` : 'None'} onClick={() => set({ reward_bonus_amount: p })} color={Number(m.reward_bonus_amount) === p ? 'primary' : 'default'} />)}
                    </Box>
                </Stack>
            </Section>
            <Section title="Free subscription" hint="Switched on for the customer the moment they claim. ₹0 paid, and it shows in their subscription history as a Moment reward.">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <FormControl size="small" fullWidth error={!!errors.reward_plan_id}>
                        <InputLabel>Plan</InputLabel>
                        <Select label="Plan" value={m.reward_plan_id || ''}
                            onChange={(e) => set({
                                reward_plan_id: e.target.value || null,
                                ...(e.target.value && !m.reward_plan_id && m.cta_target === 'SubscriptionScreen' ? { cta_target: 'ManageSubscriptionsPage' } : {}),
                            })}>
                            <MenuItem value="">None</MenuItem>
                            {(options.plans || []).map((p) => (
                                <MenuItem key={p.id} value={p.id} disabled={planCoverage(p, hubIds) === 'none' && p.id !== m.reward_plan_id}>
                                    {planOptionLabel(p, hubIds)}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.reward_plan_id && <Typography sx={{ color: 'error.main', fontSize: 12, mt: .5 }}>{errors.reward_plan_id}</Typography>}
                    </FormControl>
                    {!!m.reward_plan_id && (
                        <FormControl size="small" fullWidth error={!!errors.reward_plan_days}>
                            <InputLabel>How long</InputLabel>
                            <Select label="How long" value={m.reward_plan_days ?? 'plan'}
                                onChange={(e) => set({ reward_plan_days: e.target.value === 'plan' ? null : e.target.value })}>
                                {PLAN_LENGTHS.map((l) => (
                                    <MenuItem key={l.label} value={l.value ?? 'plan'} disabled={!l.value && !!chosenPlan?.short}>
                                        {!l.value && chosenPlan ? `${l.label} (${chosenPlan.length})` : l.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.reward_plan_days && <Typography sx={{ color: 'error.main', fontSize: 12, mt: .5 }}>{errors.reward_plan_days}</Typography>}
                        </FormControl>
                    )}
                </Stack>
                {!!m.reward_plan_id && !errors.reward_plan_id && preview?.plan && (
                    <Box sx={{ mt: 1.5, border: `1px solid ${LINE}`, borderRadius: 2, overflow: 'hidden' }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, px: 1.25, pt: 1 }}>Who gets what</Typography>
                        {planBreakdown(preview.plan).map(([label, count, hint]) => (
                            <Box key={label} sx={{ px: 1.25, py: .75, borderTop: `1px solid ${LINE}`, '&:first-of-type': { borderTop: 0 } }}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(count || 0).toLocaleString('en-IN')}</Typography>
                                </Stack>
                                <Typography sx={{ fontSize: 12, color: MUTED }}>{hint}</Typography>
                            </Box>
                        ))}
                    </Box>
                )}
                {!!m.reward_plan_id && !errors.reward_plan_id && (preview?.plan?.not_offered > 0) && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        If your message mentions {planName}, those {preview.plan.not_offered.toLocaleString('en-IN')} customers read that too. Keep the plan out of the words, or pick hubs that all sell it.
                    </Alert>
                )}
                {!!m.reward_plan_id && (
                    <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>
                        Already on this plan: it is extended. On a different plan: the free one starts when their current plan ends, so nobody loses paid days.
                    </Typography>
                )}
            </Section>
            {!!fullRewardText(m, planName) && (
                <>
                    <Alert severity="success" icon={false} sx={{ mb: 2 }}>
                        Each customer gets <b>{fullRewardText(m, planName)}</b>. Use <code>{'{points}'}</code> and <code>{'{bonus}'}</code> in the message to show it.
                    </Alert>
                    <Section title="Rewards expire after">
                        <Select size="small" value={m.reward_expiry_days} onChange={(e) => set({ reward_expiry_days: e.target.value })} sx={{ minWidth: 160 }}>
                            {[7, 14, 30, 60, 90, 180, 365].map((d) => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
                        </Select>
                    </Section>
                    <Section title="Credit the reward">
                        <ToggleButtonGroup size="small" exclusive value={m.reward_on} orientation={narrow ? 'vertical' : 'horizontal'} fullWidth={narrow} onChange={(_, v) => v && set({ reward_on: v })}>
                            <ToggleButton value="tap">When they tap the button (recommended)</ToggleButton>
                            <ToggleButton value="send">To everyone on send</ToggleButton>
                        </ToggleButtonGroup>
                        <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>
                            {m.reward_on === 'tap' ? 'You only pay for customers who saw it. Each customer can claim once.' : 'Every customer gets the reward straight away, even if they never open the app.'}
                        </Typography>
                    </Section>
                </>
            )}
        </>
    );

    const delivery = (
        <>
            <Section title="Channels">
                <Stack>
                    <FormControlLabel control={<Switch checked={m.send_push} onChange={(e) => set({ send_push: e.target.checked })} />} label="Push notification with the image (Android & iOS)" />
                    <FormControlLabel control={<Switch checked={m.send_popup} onChange={(e) => set({ send_popup: e.target.checked })} />} label="In-app popup: instantly if the app is open, otherwise on next open" />
                    <FormControlLabel control={<Switch checked={m.send_inbox} onChange={(e) => set({ send_inbox: e.target.checked })} />} label="Notification bell entry" />
                </Stack>
                {errors.send_push && <Typography sx={{ color: 'error.main', fontSize: 12 }}>{errors.send_push}</Typography>}
                <Typography sx={{ fontSize: 12, color: MUTED, mt: .5 }}>A customer sees at most one popup a day. Each moment pops up once, then stays in the bell.</Typography>
            </Section>
            <Section title="When">
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField size="small" type="datetime-local" label="Schedule (India time)" InputLabelProps={{ shrink: true }} value={m.scheduled_at}
                        onChange={(e) => set({ scheduled_at: e.target.value })} helperText="Empty = send now" error={!!errors.scheduled_at} fullWidth={narrow} />
                    <TextField size="small" type="datetime-local" label="Stop showing popup" InputLabelProps={{ shrink: true }} value={m.popup_until}
                        onChange={(e) => set({ popup_until: e.target.value })} helperText={errors.popup_until || 'Empty = until seen'} error={!!errors.popup_until} fullWidth={narrow} />
                </Stack>
                <FormControlLabel sx={{ mt: 1 }} control={<Switch checked={m.quiet_hours} onChange={(e) => set({ quiet_hours: e.target.checked })} />} label="Quiet hours: hold pushes from 9 PM to 8 AM" />
            </Section>
        </>
    );

    /** Audience and reward cannot change once a moment went out. */
    const locked = (panel, what) => (liveEdit ? (
        <>
            <Alert severity="info" sx={{ mb: 2 }}>
                The {what} is locked because this moment already went out and people may have claimed it. Duplicate it to change the {what}.
            </Alert>
            <Box sx={{ opacity: .55, pointerEvents: 'none' }} aria-disabled="true">{panel}</Box>
        </>
    ) : panel);
    const panels = [design, locked(audience, 'audience'), locked(reward, 'reward'), delivery];

    const summary = (
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 3, p: 1.5, bgcolor: '#FCFAFD' }}>
            {previewing && <LinearProgress sx={{ mb: 1 }} />}
            <Grid container spacing={1}>
                {[
                    ['Customers', preview ? preview.reach.toLocaleString('en-IN') : '…'],
                    ['Push reach', preview ? (m.send_push ? preview.push_reach.toLocaleString('en-IN') : 'Off') : '…'],
                    ['Reward cost', preview ? (costText(preview) || 'None') : '…'],
                ].map(([k, v]) => (
                    <Grid item xs={4} key={k}>
                        <Typography sx={{ fontSize: 11, color: MUTED }}>{k}</Typography>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: 15, sm: 18 } }}>{v}</Typography>
                    </Grid>
                ))}
            </Grid>
            {preview?.label && <Typography sx={{ fontSize: 12, color: MUTED, mt: .5 }}>{preview.label}</Typography>}
            {preview?.needs_super_admin && <Alert severity="info" sx={{ mt: 1 }}>More than 1,000 customers: only a super admin can send this.</Alert>}
            {preview?.quiet_now && m.send_push && !m.scheduled_at && <Alert severity="info" sx={{ mt: 1 }}>It is quiet hours now: the push will wait until 8 AM. The popup and bell are immediate.</Alert>}
            {preview && preview.reach === 0 && !blockers.length && <Alert severity="warning" sx={{ mt: 1 }}>Nobody matches this audience.</Alert>}
            {blockers.map((b) => <Alert key={b} severity="error" sx={{ mt: 1 }}>{b}</Alert>)}
        </Box>
    );

    const phone = (
        <Box sx={{ position: wide ? 'sticky' : 'static', top: 16 }}>
            <Stack direction="row" justifyContent="center" sx={{ mb: 1.5 }}>
                <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
                    <ToggleButton value="push">Push</ToggleButton>
                    <ToggleButton value="popup">Popup</ToggleButton>
                    <ToggleButton value="bell">Bell</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
            {persons.length > 1 && (
                <Stack direction="row" justifyContent="center" spacing={.75} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                    {persons.map((p, i) => <Chip key={`${p.first_name}${i}`} size="small" label={`As ${p.first_name}`} color={i === personIdx ? 'primary' : 'default'} onClick={() => setPersonIdx(i)} />)}
                </Stack>
            )}
            <PhonePreview m={m} person={person} mode={mode} planName={planName} />
            {mode === 'popup' && !!fullRewardText(m, planName) && <Typography sx={{ textAlign: 'center', fontSize: 12, color: MUTED, mt: 1 }}>Tap the button to see the claimed state.</Typography>}
        </Box>
    );

    if (view === 'list') {
        return (
            <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Toast ref={toast} />
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: 20, sm: 24 }, color: INK }}>Moments</Typography>
                        <Typography sx={{ color: MUTED, fontSize: 14 }}>Personal pushes, instant popups and bonus points for the customers you choose.</Typography>
                    </Box>
                    <Button variant="contained" startIcon={<AutoAwesomeOutlinedIcon />} onClick={startNew} sx={{ bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' }, alignSelf: { xs: 'stretch', sm: 'auto' } }}>New moment</Button>
                </Stack>
                <MomentList rows={rows} loading={loading} onNew={startNew} onAction={onAction} />
                <ConfirmDialog
                    open={!!confirm}
                    copy={confirm ? CONFIRM_COPY[confirm.kind](confirm.row) : null}
                    busy={confirmBusy}
                    onCancel={() => setConfirm(null)}
                    onConfirm={runConfirm}
                />
            </Box>
        );
    }

    const totalCost = costText(preview);
    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Toast ref={toast} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Button onClick={() => { setView('list'); load(); }}>← Moments</Button>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: 18, sm: 22 }, flex: 1 }} noWrap>{liveEdit ? 'Edit live moment' : editingId ? 'Edit moment' : 'New moment'}</Typography>
            </Stack>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={7}>
                    <Tabs value={step} onChange={(_, v) => setStep(v)} variant={narrow ? 'scrollable' : 'fullWidth'} scrollButtons="auto" sx={{ mb: 2, borderBottom: `1px solid ${LINE}` }}>
                        {STEPS.map((s, i) => <Tab key={s} label={`${i + 1}. ${s}`} />)}
                    </Tabs>
                    {panels[step]}
                    {!wide && <Box sx={{ my: 3 }}>{phone}</Box>}
                    <Box sx={{ mt: 2 }}>{summary}</Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
                        {step > 0 && <Button onClick={() => setStep(step - 1)}>Back</Button>}
                        <Box sx={{ flex: 1 }} />
                        {liveEdit ? (
                            <Button variant="contained" onClick={async () => { if (await save()) { setView('list'); load({ silent: true }); } }} disabled={busy} sx={{ bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' } }}>Save changes</Button>
                        ) : (<>
                        <Button variant="outlined" onClick={saveDraft} disabled={busy}>Save draft</Button>
                        <Button variant="outlined" onClick={() => setTestOpen(true)} disabled={busy}>Send test</Button>
                        {step < STEPS.length - 1
                            ? <Button variant="contained" onClick={() => setStep(step + 1)} sx={{ bgcolor: INK }}>Next</Button>
                            : <Button variant="contained" startIcon={<SendRoundedIcon />} disabled={busy || !preview || preview.reach === 0 || blockers.length > 0} onClick={() => setSendOpen(true)} sx={{ bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' } }}>{m.scheduled_at ? 'Schedule' : 'Send'}</Button>}
                        </>)}
                    </Stack>
                </Grid>
                {wide && <Grid item lg={5}>{phone}</Grid>}
            </Grid>

            <Dialog open={testOpen} onClose={() => setTestOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Send a test</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14, color: MUTED, mb: 1.5 }}>Shows it on one phone. Nothing is recorded, no points are given, and that customer still gets the real one later.</Typography>
                    <TextField autoFocus fullWidth size="small" label="Customer mobile number" placeholder="Leave empty for your own account" value={testMobile} onChange={(e) => setTestMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} />
                </DialogContent>
                <DialogActions><Button onClick={() => setTestOpen(false)}>Close</Button><Button variant="contained" onClick={sendTest}>Send test</Button></DialogActions>
            </Dialog>

            <Dialog open={sendOpen} onClose={() => setSendOpen(false)} fullWidth maxWidth="xs" fullScreen={narrow}>
                <DialogTitle>{m.scheduled_at ? 'Schedule this moment?' : 'Send this moment now?'}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        {preview?.reach?.toLocaleString('en-IN')} customers
                        {m.send_push ? ` · push to ${preview?.push_reach?.toLocaleString('en-IN')}` : ''}
                        {m.send_popup ? ' · popup' : ''}{m.send_inbox ? ' · bell' : ''}
                    </Typography>
                    {!!totalCost && <Typography sx={{ mb: 1 }}>Up to <b>{totalCost}</b> ({rewardText(m.reward_points, m.reward_bonus_amount)} each, {m.reward_on === 'tap' ? 'credited when tapped' : 'credited now'}).</Typography>}
                    {!!m.reward_plan_id && (
                        <Typography sx={{ mb: 1 }}>
                            Plus a free <b>{planName}</b> plan for each customer who {m.reward_on === 'tap' ? 'claims' : 'receives'} it
                            {preview?.plan?.not_offered ? `, except ${preview.plan.not_offered.toLocaleString('en-IN')} whose hub doesn't sell it` : ''}
                            {preview?.plan?.left_out ? `. ${preview.plan.left_out.toLocaleString('en-IN')} left out: their hub doesn't sell it` : ''}.
                        </Typography>
                    )}
                    {preview?.needs_cost_confirmation && (
                        <FormControlLabel control={<Checkbox checked={confirmCost} onChange={(e) => setConfirmCost(e.target.checked)} />} label="I understand the cost" />
                    )}
                    <Typography sx={{ fontSize: 12, color: MUTED }}>After sending, the moment can't be edited. You can stop the popup at any time.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendOpen(false)}>Back</Button>
                    <Button variant="contained" onClick={send} disabled={busy || (preview?.needs_cost_confirmation && !confirmCost)} sx={{ bgcolor: PINK, '&:hover': { bgcolor: '#C8155F' } }}>
                        {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : (m.scheduled_at ? 'Schedule' : 'Send now')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MomentsStudio;
